import { createContext, Dispatch, FC, PropsWithChildren, useCallback, useState } from 'react'

import { ConfigProvider, theme, ThemeConfig } from 'antd'
import { useResponsive } from 'antd-style'
import en from 'antd/locale/en_US'
import uk from 'antd/locale/uk_UA'

import { Context as IntlContext } from './Intl'

const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorLinkHover: '#e87b27',
    colorLink: '#FF6600',
    colorBgLayout: '#191817',
    colorBgContainer: '#151413',
    colorPrimary: '#FF6600',
  },
  components: {
    Table: {
      rowHoverBg: '#191817',
      headerBg: '#100908',
    },
  },
}

const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorLinkHover: '#e87b27',
    colorLink: '#FF6600',
    colorPrimary: '#FF6600',
    colorBorder: '#969696',
    colorBorderSecondary: '#e1dcd7',
  },
}
type Theme = 'dark' | 'light'
type Size = 'small' | 'default' | 'large'

type ContextProps = { theme: Theme; setTheme: Dispatch<Theme>; size: Size; setSize: Dispatch<Size> }

const defaultValue: ContextProps = {
  theme: 'dark',
  setTheme: () => undefined,
  size: 'default',
  setSize: () => undefined,
}

const locales = { en, uk }

const Context = createContext(defaultValue)

function getSize(size: Size): number {
  switch (size) {
    case 'small':
      return 14
    case 'large':
      return 18
    case 'default':
    default:
      return 16
  }
}

function useTheme(theme: Theme, size: Size): ThemeConfig {
  const { mobile } = useResponsive()
  const { token, components, algorithm } = theme === 'light' ? lightTheme : darkTheme
  const fontSize = getSize(size)

  return {
    algorithm,
    token: {
      fontFamily: 'Fira Sans',
      fontFamilyCode: 'Russo One',
      fontSize,
      controlHeight: mobile ? 32 : 40,
      borderRadius: 0,
      fontSizeHeading1: mobile ? fontSize * 1.5 : fontSize * 2,
      fontSizeHeading2: mobile ? fontSize * 1.375 : fontSize * 1.75,
      fontSizeHeading3: mobile ? fontSize * 1.25 : fontSize * 1.5,
      fontSizeHeading4: mobile ? fontSize * 1.125 : fontSize * 1.25,
      fontSizeHeading5: fontSize,
      ...token,
    },
    components: {
      Menu: { darkItemBg: '#100908', darkPopupBg: 'rgba(21,22,24,0.7)', subMenuItemBg: '#100908', ...components?.Menu },
      Layout: {
        footerPadding: mobile ? '16px 24px' : '16px 40px',
        headerPadding: mobile ? '0 24px' : '0 40px',
        headerHeight: mobile ? 64 : 72,
        headerBg: '#100908',
        footerBg: '#100908',
        ...components?.Layout,
      },
      ...components,
    },
  }
}

const Config: FC<PropsWithChildren<{ defaultTheme?: Theme; defaultSize?: Size }>> = ({
  defaultTheme = defaultValue.theme,
  defaultSize = defaultValue.size,
  children,
}) => {
  const [theme, setTheme] = useState<ContextProps['theme']>(
    localStorage.getItem('theme') ? (`${localStorage.getItem('theme')}` as Theme) : defaultTheme,
  )
  const [size, setSize] = useState<ContextProps['size']>(
    localStorage.getItem('size') ? (`${localStorage.getItem('size')}` as Size) : defaultSize,
  )
  const uiTheme = useTheme(theme, size)

  const switchTheme = useCallback((value: Theme) => {
    localStorage.setItem('theme', value)
    setTheme((prevState) => (prevState === 'dark' ? 'light' : 'dark'))
  }, [])

  const switchSize = useCallback((value: Size) => {
    localStorage.setItem('size', value)
    setSize(value)
  }, [])

  return (
    <Context.Provider
      value={{
        theme,
        setTheme: switchTheme,
        size,
        setSize: switchSize,
      }}
    >
      <IntlContext.Consumer>
        {({ locale }) => (
          <ConfigProvider locale={locales[locale]} theme={uiTheme}>
            {children}
          </ConfigProvider>
        )}
      </IntlContext.Consumer>
    </Context.Provider>
  )
}

export { Config, Context }
